import React, {useMemo} from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath, Redirect, Route, useParams, useRouteMatch} from 'react-router-dom';
import {toast} from 'react-toastify';

import {ReactComponent as CopyIcon} from 'assets/svg/copy.svg';
import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {ProductGroupIcon} from 'components/common/ProductGroupIcon';
import {Icon} from 'components/ui-kit/Icon';
import {Link} from 'components/ui-kit/Link';
import {
    ContentContainer,
    CopyButton,
    Id,
    StatusWrapper,
    Title,
    TitleText,
    TitleWrapper,
} from 'components/ui-kit/PageComponents';
import {RouterSwitch, RouterTab, RouterTabList} from 'components/ui-kit/RouterTabs';
import {AUTHENTIC_STATUS_COLORS, PRODUCT_STATUS_COLORS} from 'core/constants/statuses';
import {AuthenticStatus, ProductStatus} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {commonDataState$, Gate} from 'models/products/detail';
import {colors} from 'theme';
import {AggregationStorageLocation, UserPermission} from 'types';
import {CUSTOMER_PACKAGE_TYPES, PackageTypes, TRANSPORT_PACKAGE_TYPES} from 'types/gtins';
import {ProductCommonRaw, ProductFormatted} from 'types/products-detail';
import {useClipboard} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {formatAddress} from 'utils/formatAddress';
import {handleNil} from 'utils/handleNil';
import {fromHex, toHex} from 'utils/string';

import {AggregateContent} from './AggregateContent';
import {Blocking} from './Blocking';
import {CommonInfo} from './CommonInfo';
import {Operations} from './Operations';
import {Remarking} from './Remarking';
import {
    CardInfoLine,
    CardInfoText,
    CommonInfoChildren,
    CommonInfoChildrenTitle,
    CustomLink,
    Separator,
} from './styled';
import {Tracking} from './Tracking';

export function convertData(data: ProductCommonRaw | null, t: TFunction): ProductFormatted {
    const result: ProductFormatted = {
        code: handleNil(data?.markingCode),
        ...(data?.packagingType &&
            !TRANSPORT_PACKAGE_TYPES.includes(data?.packagingType) && {
                batchNumber: handleNil(data?.product?.batchNumber),
            }),
        fileLink: data?.markingCode
            ? encodeURIComponent(data?.markingCode?.replace('\\u001D', ''))
            : '#',
        name: handleNil(data?.product?.productName),
        brand: handleNil(data?.product?.productTrademark),
        productGroupCode: handleNil(data?.product?.productGroupCode, (value) => (
            <ProductGroupIcon value={value} label />
        )),
        packagingType: handleNil(data?.packagingType, (value: PackageTypes) =>
            t(`dictionaries.products.packageType.${value}`),
        ),
        manufacturingDate: handleNil(data?.product?.manufacturingDate, (value: string) =>
            formatDate(value, 'dd.MM.yyyy'),
        ),
        gtin: handleNil(data?.product?.gtin),
        packagingLevel: handleNil(data?.packagingLevel),
        isAggregate: data?.packagingLevel !== 0,
        isGroup: data?.packagingLevel !== 0 && data?.packagingType === 'GROUP',
        ...(data?.packagingType && {
            isTransportPackage: TRANSPORT_PACKAGE_TYPES.includes(data?.packagingType),
        }),
        status: handleNil(data?.status, (value: string) =>
            t(`dictionaries.products.status.${value}`),
        ),
        generatedTime: '—',
        issuedTime: '—',
        appliedTime: '—',
        aggregationInfo: '—',
        enteredCirculationTime: '—',
        leftCirculationTime: '—',
        storageLocation: handleNil(data?.storageLocation, (value: AggregationStorageLocation) =>
            formatAddress(value, {toDisplay: ['city', 'address'], storage: false}),
        ),
        storageName: handleNil(data?.storageLocation?.storageName),
        authentic: {
            status: (
                <StatusWrapper
                    color={
                        AUTHENTIC_STATUS_COLORS[
                            data?.markingCodeAuthenticInfo?.authenticStatus ?? 'DEFAULT'
                        ]
                    }
                >
                    {data?.markingCodeAuthenticInfo?.authenticStatus
                        ? t(
                              `dictionaries.authenticStatus.${data?.markingCodeAuthenticInfo?.authenticStatus}`,
                          )
                        : '—'}
                </StatusWrapper>
            ),
            date: data?.markingCodeAuthenticInfo?.authenticDetectionDateTime
                ? formatDate(
                      data?.markingCodeAuthenticInfo.authenticDetectionDateTime,
                      'dd.MM.yyyy, HH:mm',
                  )
                : '—',
            type: data?.markingCodeAuthenticInfo?.authenticDetectionContext
                ? t(
                      `pages.products.detail.common.authenticContext.${data?.markingCodeAuthenticInfo?.authenticDetectionContext}`,
                  )
                : '—',
            link: data?.markingCodeAuthenticInfo?.documentId ? (
                <Link to={`/receipts/${data?.markingCodeAuthenticInfo?.documentId}`}>
                    {t('pages.products.detail.common.documentLink')}
                </Link>
            ) : (
                '—'
            ),
        },
    };

    if (data?.statuses) {
        Object.values(data.statuses).forEach((elem) => {
            switch (elem.status) {
                case ProductStatus.GENERATED:
                    result.generatedTime = formatDate(elem.operationTime, 'dd MMMM yyyy, HH:mm');
                    break;
                case ProductStatus.ISSUED:
                    result.issuedTime = formatDate(elem.operationTime, 'dd MMMM yyyy, HH:mm');
                    break;
                case ProductStatus.APPLIED:
                    result.appliedTime = formatDate(elem.operationTime, 'dd MMMM yyyy, HH:mm');
                    break;
                case ProductStatus.AGGREGATED:
                    result.aggregationInfo = (
                        <>
                            {formatDate(elem.operationTime, 'dd MMMM yyyy, HH:mm')}
                            {elem.parentMarkingCode && (
                                <>
                                    {`, ${t('pages.products.detail.common.inPackage')} `}
                                    <CustomLink
                                        to={generatePath(ROUTE.PRODUCTS_DETAIL, {
                                            id: toHex(`${null}§${elem.parentMarkingCode}`),
                                        })}
                                        style={{wordBreak: 'break-all'}}
                                    >
                                        {elem.parentMarkingCode}
                                    </CustomLink>
                                </>
                            )}
                            {!elem.parentMarkingCode &&
                                data.packagingLevel !== 0 &&
                                `, ${t('pages.products.detail.common.topLevelPackage')}`}
                        </>
                    );
                    break;
                case ProductStatus.INTRODUCED:
                    result.enteredCirculationTime = formatDate(
                        elem.operationTime,
                        'dd MMMM yyyy, HH:mm',
                    );
                    break;
                case ProductStatus.SOLD:
                case ProductStatus.DROPOUT:
                    result.leftCirculationTime = formatDate(
                        elem.operationTime,
                        'dd MMMM yyyy, HH:mm',
                    );
                    break;
            }
        });
    }

    return result;
}

interface ProductsDetailPageProps {
    backTo?: string;
    backLabel?: string;
}

export const ProductsDetailPage = ({backTo, backLabel}: ProductsDetailPageProps = {}) => {
    const {t} = useTranslation();
    const {path, url} = useRouteMatch();
    const {id: rawId}: {id: string} = useParams();
    const copy = useClipboard();

    const id = fromHex(rawId);
    let gtin: string | null = null;
    let markingCode = 'invalid';

    if (id) {
        const separatorPosition = id.indexOf('§');
        gtin = id.substring(0, separatorPosition);
        gtin = gtin === 'null' ? null : gtin;
        markingCode = id.substring(separatorPosition + 1);
    }

    const {data, error, isLoading} = useStore(commonDataState$);
    const hasRemarking = Boolean(data?.replacedByMarkingCodeId || data?.replacesMarkingCodeId);
    const hasBlocking = Boolean(data?.blockings.length);

    const mappedData = React.useMemo(() => convertData(data, t), [data, t]);

    const backRoute = useMemo(() => {
        const type = data?.packagingType;
        if (!type || CUSTOMER_PACKAGE_TYPES.includes(type)) return ROUTE.PRODUCTS_CONSUMER_GRID;
        if (TRANSPORT_PACKAGE_TYPES.includes(type)) return ROUTE.PRODUCTS_TRANSPORT_GRID;
        return ROUTE.PRODUCTS_CUSTOMS_GRID;
    }, [data]);

    const copyCode = () => {
        copy(markingCode);
        toast.success(t('pages.products.detail.common.markingCodeCopied'));
    };

    const packageLevelText = useMemo(
        () =>
            data?.parentMarkingCode
                ? t('pages.products.detail.common.packagingLevels.1')
                : t('pages.products.detail.common.packagingLevels.0'),
        [data?.packagingLevel, t],
    );

    return (
        <>
            <Gate gtin={gtin} markingCode={markingCode} />
            {backTo ? (
                <BackTo to={backTo}>{backLabel ?? t('pages.products.detail.back')}</BackTo>
            ) : (
                <PermissionAccess
                    permissions={[
                        UserPermission.PRODUCT_GRID_VIEW,
                        UserPermission.ALL_PRODUCT_GRID_VIEW,
                    ]}
                >
                    <BackTo to={backRoute}>{t('pages.products.detail.back')}</BackTo>
                </PermissionAccess>
            )}
            <Page title={t('pages.products.detail.common.markingCodes')}>
                <ContentContainer
                    status={isLoading ? 'loading' : error != null ? 'error' : 'default'}
                >
                    <TitleWrapper>
                        <TitleText>
                            <Title>
                                {t('pages.products.detail.common.markingCode')}{' '}
                                <Id>{mappedData.code}</Id>
                            </Title>
                            <CopyButton
                                icon={
                                    <Icon
                                        source={CopyIcon}
                                        width={15}
                                        height={15}
                                        fillColor={colors.brandPrimary}
                                    />
                                }
                                onClick={copyCode}
                                hoverIndication={true}
                                label={t('pages.products.detail.common.copyCode')}
                                labelPlace={'bottom'}
                            />
                            {data?.markingCodeAuthenticInfo?.authenticStatus ===
                            AuthenticStatus.AUTHENTIC ? (
                                <CardInfoLine>
                                    <Separator />
                                    {data?.parentMarkingCode ? (
                                        <CardInfoText>
                                            {packageLevelText}{' '}
                                            <Link
                                                to={generatePath(ROUTE.PRODUCTS_DETAIL, {
                                                    id: toHex(`${null}§${data.parentMarkingCode}`),
                                                })}
                                                style={{wordBreak: 'break-all'}}
                                            >
                                                {data.parentMarkingCode}
                                            </Link>
                                        </CardInfoText>
                                    ) : (
                                        <CardInfoText>{packageLevelText}</CardInfoText>
                                    )}
                                </CardInfoLine>
                            ) : null}
                        </TitleText>
                        <div style={{marginLeft: 'auto'}}>{data?.owner}</div>
                        <StatusWrapper color={PRODUCT_STATUS_COLORS[data?.status ?? 'DEFAULT']}>
                            {mappedData.status}
                        </StatusWrapper>
                    </TitleWrapper>

                    {/*<ColumnsWrapper>*/}
                    {/*    <Column>*/}
                    {/*        <HeaderTypography>*/}
                    {/*            {t('pages.products.detail.common.sourcePhoto')}:*/}
                    {/*        </HeaderTypography>*/}
                    {/*        <OriginalPhotoModal code={mappedData.code} link={mappedData.fileLink} />*/}
                    {/*    </Column>*/}
                    {/*</ColumnsWrapper>*/}

                    <RouterTabList baseUrl={url}>
                        <RouterTab url="">{t('pages.products.detail.common.commonInfo')}</RouterTab>

                        {/* {(mappedData.isAggregate || mappedData.isGroup) && (
                            <RouterTab url="content">
                                {t(
                                    `pages.products.detail.common.${
                                        mappedData.isGroup ? 'groupContent' : 'aggregateContent'
                                    }`,
                                )}
                            </RouterTab>
                        )} */}

                        <RouterTab url="operations">
                            {t('pages.products.detail.common.operations')}
                        </RouterTab>

                        <RouterTab url="tracking">
                            {t('pages.products.detail.common.tracing')}
                        </RouterTab>

                        {hasRemarking && (
                            <RouterTab url="remarking">
                                {t('pages.products.detail.common.remarking')}
                            </RouterTab>
                        )}

                        {hasBlocking && (
                            <RouterTab url="blocking">
                                {t('pages.products.detail.common.blocking')}
                            </RouterTab>
                        )}
                    </RouterTabList>

                    <RouterSwitch rootPath={url}>
                        <Route path={`${path}`} exact>
                            <CommonInfo
                                data={mappedData}
                                isLoading={isLoading}
                                packagingType={data?.packagingType}
                            />
                            {data?.childrenTotalCount ? (
                                <CommonInfoChildren>
                                    <CommonInfoChildrenTitle>
                                        {t('pages.products.detail.nestedCodes')}
                                    </CommonInfoChildrenTitle>
                                    <AggregateContent markingCode={markingCode} gtin={gtin} />
                                </CommonInfoChildren>
                            ) : null}
                        </Route>

                        {/* <Route path={`${path}/content`} exact>
                            <AggregateContent markingCode={markingCode} gtin={gtin} />
                        </Route> */}

                        <Route path={`${path}/operations`} exact>
                            <Operations />
                        </Route>

                        <Route path={`${path}/tracking`} exact>
                            <Tracking />
                        </Route>

                        <Route path={`${path}/remarking`} exact>
                            <Remarking />
                        </Route>

                        <Route path={`${path}/blocking`} exact>
                            {data?.blockings.length === 0 ? <Redirect to={url} /> : <Blocking />}
                        </Route>
                    </RouterSwitch>
                </ContentContainer>
            </Page>
        </>
    );
};
